(function () {
    'use strict';

    if (window.WCWYDrawerController) return;

    var registry = {};
    var activeKey = null;
    var isLocked = false;

    /* One source of truth for drawer motion: the CSS custom properties in
       wcwy-layout.css. JS used to carry its own 360ms / 420ms magic numbers,
       which silently drifted every time the CSS duration changed. Read once,
       lazily, and hand back milliseconds. */
    var timingCache = null;

    function readMs(styles, name, fallback) {
        var raw = (styles.getPropertyValue(name) || '').trim();
        if (!raw) return fallback;
        var value = parseFloat(raw);
        if (isNaN(value)) return fallback;
        return raw.indexOf('ms') === -1 && raw.indexOf('s') !== -1 ? value * 1000 : value;
    }

    function timing() {
        if (timingCache) return timingCache;
        var styles = window.getComputedStyle(document.documentElement);
        var open = readMs(styles, '--wcwy-drawer-duration', 360);
        var close = readMs(styles, '--wcwy-drawer-duration-out', 260);
        timingCache = {
            open: open,
            close: close,
            /* Focus lands as the panel settles; the fallback fires only if
               transitionend never does (interrupted transition, hidden tab). */
            openSettle: Math.round(open + 20),
            closeFallback: Math.round(close + 80)
        };
        return timingCache;
    }

    function getEntry(key) {
        return registry[key] || null;
    }

    /**
     * Run fn after the browser has completed a full frame — layout AND paint.
     *
     * Why this exists: every drawer is hidden with content-visibility/visibility
     * until it opens, so its subtree has never been laid out. The old open path
     * flipped the mount class and then, one rAF later, forced a sync layout read
     * and added .is-open in that same frame. Laying out and painting an entire
     * drawer (cart line items, menu views, filter groups) therefore landed in
     * the exact frame the transform was supposed to start, dropping the first
     * frames of the animation. Because the ease is a decelerate — fastest at the
     * start — those dropped frames are precisely where most of the travel
     * happens, so the panel appeared to jump most of the way and then glide the
     * remainder. That is what read as "abrupt on open", and it is also why
     * closing felt fine: closing has no layout to do.
     *
     * One rAF is not enough. A callback in rAF runs *before* that frame's paint,
     * so work done there still competes with the frame it is supposed to start
     * clean. Two nested rAFs put fn at the start of the frame after the paint.
     */
    function afterPaint(fn) {
        return window.requestAnimationFrame(function () {
            window.requestAnimationFrame(fn);
        });
    }

    /**
     * Deferred first mount — "prime" a drawer's subtree.
     *
     * afterPaint above solved this problem at the wrong scale. Moving the mount
     * one frame earlier still pays for the drawer's first layout, paint and
     * image decode inside the interaction, and the enter ease is a decelerate:
     * it spends 60% of its travel in the first 250ms, so whatever frames that
     * work eats are exactly the frames the eye reads as motion. It is also why
     * the second open is better but never clean — some of the work is cached,
     * and the rest is redone because closing tore the subtree back down.
     *
     * The mobile menu is the only drawer that feels right and the only one
     * revealed at init and never hidden again. By the time it opens there is
     * nothing left to do but a transform. Every drawer now gets that: reveal
     * once, at idle, and never tear it down on close.
     *
     * Scheduling rules that matter:
     * - after `load`, so the reveal cannot compete with FCP or LCP;
     * - one job per idle callback, so three drawers are three short tasks
     *   instead of one long one (anything over 50ms lands in Total Blocking
     *   Time, and a long task here would trade a real INP win for a lab-metric
     *   regression);
     * - with a timeout, so a permanently busy page still primes eventually.
     *
     * The cost this buys back is bounded by `contain: layout paint` on each
     * panel: a primed-but-closed drawer cannot dirty the page's layout tree on
     * later recalcs, so the layout is paid once rather than on every
     * invalidation for the life of the page.
     *
     * One thing a caller MUST do: `visibility: hidden` was also keeping the
     * closed drawer out of the tab order and the accessibility tree. A primed
     * drawer is visible, so it has to be held `inert` while shut — see
     * setInert. Without that, a closed cart puts its buttons in the tab order
     * and pairs focusable content with aria-hidden="true".
     */
    var primeQueue = [];
    var primeScheduled = false;

    function runPrimeQueue() {
        primeScheduled = false;
        var job = primeQueue.shift();
        if (job) {
            try {
                job();
            } catch (err) {
                /* One drawer failing to prime must not strand the others. */
            }
        }
        schedulePrimeQueue();
    }

    function schedulePrimeQueue() {
        if (primeScheduled || !primeQueue.length) return;
        primeScheduled = true;
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(runPrimeQueue, { timeout: 2000 });
        } else {
            window.setTimeout(runPrimeQueue, 200);
        }
    }

    function prime(job) {
        if (typeof job !== 'function') return;
        primeQueue.push(job);
        if (document.readyState === 'complete') {
            schedulePrimeQueue();
        } else {
            /* Identical (type, listener, capture) registrations are discarded
               by addEventListener, so every caller can add this safely. */
            window.addEventListener('load', schedulePrimeQueue, { once: true });
        }
    }

    /* Mirrors wcwy-mobile-drawer.js: set both the property and the attribute so
       the state survives browsers that support one but not the other. */
    function setInert(node, shouldBeInert) {
        if (!node) return;
        node.inert = shouldBeInert;
        if (shouldBeInert) {
            node.setAttribute('inert', '');
        } else {
            node.removeAttribute('inert');
        }
    }

    function getFocusable(entry) {
        if (!entry || !entry.drawer) return [];
        return Array.prototype.slice.call(entry.drawer.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(function (node) {
            return node.getAttribute('aria-hidden') !== 'true'
                && !node.hidden
                && node.getClientRects().length > 0;
        });
    }

    function focusDrawer(entry) {
        if (!entry || entry.manageFocus === false || !entry.drawer) return;
        var focusTarget = entry.focusTarget
            ? entry.drawer.querySelector(entry.focusTarget)
            : null;
        var focusable = getFocusable(entry);
        var target = focusTarget || focusable[0] || entry.drawer;

        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                if (target && typeof target.focus === 'function') {
                    if (!target.hasAttribute('tabindex') && target === entry.drawer) {
                        target.setAttribute('tabindex', '-1');
                    }
                    target.focus({ preventScroll: true });
                }
            });
        });
    }

    function restoreFocus(entry) {
        if (!entry || entry.manageFocus === false) return;
        var target = entry.lastFocusedElement;
        entry.lastFocusedElement = null;
        if (!target || typeof target.focus !== 'function' || !document.contains(target)) return;
        target.focus({ preventScroll: true });
    }

    function anyRegisteredDrawerOpen() {
        return Object.keys(registry).some(function (key) {
            var entry = registry[key];
            return entry && typeof entry.isOpen === 'function' && entry.isOpen();
        });
    }

    function lockBody() {
        if (isLocked) return;
        isLocked = true;
        document.documentElement.classList.add('is-drawer-open');
        document.body.classList.add('is-drawer-open', 'drawer-open', 'no-scroll');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
    }

    function unlockBody() {
        if (!isLocked || anyRegisteredDrawerOpen()) return;
        isLocked = false;
        document.documentElement.classList.remove('is-drawer-open');
        document.body.classList.remove('is-drawer-open', 'drawer-open', 'no-scroll');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
    }

    function closeEntry(key) {
        var entry = getEntry(key);
        if (!entry || typeof entry.close !== 'function') return;
        if (typeof entry.isOpen === 'function' && !entry.isOpen()) return;
        entry.close({ source: 'wcwy-drawer-controller' });
    }

    function closeAll(exceptKey) {
        Object.keys(registry).forEach(function (key) {
            if (key === exceptKey) return;
            closeEntry(key);
        });

        if (!exceptKey) {
            activeKey = null;
            unlockBody();
        }
    }

    function closeFallbackDrawer(key) {
        if (key === 'cart') {
            document.querySelectorAll('[data-ch-panel="cart"]').forEach(function (panel) {
                panel.classList.remove('is-open');
                panel.setAttribute('aria-hidden', 'true');
            });
            document.body.classList.remove('cart-open');
            unlockBody();
            return;
        }

        if (key === 'samstyle') {
            document.querySelectorAll('[data-samstyle-drawer], [data-samstyle-overlay]').forEach(function (node) {
                node.classList.remove('is-open');
            });
            document.body.classList.remove('samstyle-drawer-open');
            unlockBody();
            return;
        }

        if (key === 'mobile-menu') {
            document.querySelectorAll('[data-drawer]').forEach(function (drawer) {
                drawer.classList.remove('is-open');
                drawer.setAttribute('aria-hidden', 'true');
                drawer.setAttribute('inert', '');
                drawer.inert = true;
            });
            document.querySelectorAll('[data-drawer-overlay]').forEach(function (overlay) {
                overlay.classList.remove('is-visible');
            });
            document.body.classList.remove('no-scroll');
            unlockBody();
            return;
        }

        if (key === 'search') {
            document.querySelectorAll('[data-wcwy-search-overlay]').forEach(function (overlay) {
                overlay.classList.remove('is-open');
                overlay.setAttribute('aria-hidden', 'true');
                overlay.setAttribute('inert', '');
                overlay.inert = true;
            });
            document.body.classList.remove('wcwy-search-open');
            unlockBody();
        }
    }

    function closeByKey(key) {
        if (getEntry(key)) {
            closeEntry(key);
            return;
        }

        closeFallbackDrawer(key);
    }

    window.WCWYDrawerController = {
        timing: timing,
        afterPaint: afterPaint,
        prime: prime,
        setInert: setInert,

        register: function (key, options) {
            if (!key || !options) return;
            registry[key] = options;
        },

        open: function (key) {
            closeAll(key);
            var entry = getEntry(key);
            if (entry && entry.manageFocus !== false) {
                entry.lastFocusedElement = document.activeElement;
            }
            activeKey = key;
            lockBody();
            focusDrawer(entry);
        },

        close: function (key) {
            var entry = getEntry(key);
            if (activeKey === key) activeKey = null;
            unlockBody();
            restoreFocus(entry);
        },

        closeAll: closeAll
    };

    document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
            closeAll();
            return;
        }

        if (event.key !== 'Tab' || !activeKey) return;
        var entry = getEntry(activeKey);
        if (!entry || entry.manageFocus === false || !entry.isOpen || !entry.isOpen()) return;

        var focusable = getFocusable(entry);
        if (!focusable.length) {
            event.preventDefault();
            if (entry.drawer && typeof entry.drawer.focus === 'function') entry.drawer.focus();
            return;
        }

        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !entry.drawer.contains(document.activeElement))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !entry.drawer.contains(document.activeElement))) {
            event.preventDefault();
            first.focus();
        }
    });

    document.addEventListener('click', function (event) {
        var target = null;
        if (event.target && event.target.nodeType === 1) {
            target = event.target;
        } else if (event.target && event.target.parentElement) {
            target = event.target.parentElement;
        }
        if (!target || typeof target.closest !== 'function') return;

        var explicitClose = target.closest('[data-cart-close], [data-samstyle-close], [data-drawer-close], [data-search-close]');
        if (explicitClose) {
            if (explicitClose.matches('[data-cart-close]')) closeByKey('cart');
            if (explicitClose.matches('[data-samstyle-close]')) closeByKey('samstyle');
            if (explicitClose.matches('[data-drawer-close]')) closeByKey('mobile-menu');
            if (explicitClose.matches('[data-search-close]')) closeByKey('search');
            return;
        }

        if (target.matches('[data-samstyle-overlay]')) {
            closeByKey('samstyle');
            return;
        }

        if (target.matches('[data-drawer-overlay]')) {
            closeByKey('mobile-menu');
            return;
        }

        var entry = activeKey ? getEntry(activeKey) : null;
        if (!entry || typeof entry.isOpen !== 'function' || !entry.isOpen()) return;
        if (entry.drawer && entry.drawer.contains(target)) return;
        closeAll();
    }, true);
})();
